import { TOKEN_KEY, REFRESH_TOKEN_KEY, TOKEN_EXPIRES_AT_KEY } from "@/api/auth-keys";
import envConfig from "@/config/env";
import type { OrgConfigResponse } from "@/api/services/auth";

export const SESSION_EXPIRED_MESSAGE = "Session expired. Please log in again.";

export const SSO_REDIRECT_KEY = "auth:redirect_after_sso";

export function isAdminUser(user: { role?: string; roles?: string[] } | null | undefined): boolean {
  return user?.role === "admin" || (user?.roles?.includes("admin") ?? false);
}

/**
 * Returns a safe in-app redirect path, or "/" when the candidate is unsafe.
 *
 * Post-login redirect targets come from attacker-influenceable sources: the
 * ``?redirect=`` query param on /login and the value stashed in
 * sessionStorage before an SSO bounce. Replaying such a value into
 * ``navigate()`` without validation is an open-redirect: an absolute or
 * protocol-relative URL would send a freshly authenticated user to an
 * attacker-controlled page (post-login phishing).
 *
 * We accept ONLY a same-origin absolute path: a single leading "/", not a
 * protocol-relative "//" or backslash variant, free of control characters,
 * and without a URL scheme. Anything else collapses to "/".
 */
export function sanitizeRedirectPath(
  candidate: string | null | undefined,
): string {
  if (typeof candidate !== "string" || candidate === "") return "/";
  // Must be an absolute in-app path.
  if (candidate[0] !== "/") return "/";
  // Reject protocol-relative ("//evil.com") and backslash tricks
  // ("/\\evil.com" — browsers may normalise "\" to "/").
  if (candidate[1] === "/" || candidate[1] === "\\") return "/";
  // Reject control characters / whitespace that can smuggle a scheme or
  // break header/URL parsing downstream.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(candidate)) return "/";
  return candidate;
}

/**
 * UI flags derived from an organization's auth config. Exists so the Login
 * page (and any future caller) doesn't replicate the per-flag predicates
 * — and so an inconsistent backend payload (e.g. ``sso_only`` with
 * ``sso_enabled: false``) renders a coherent UI instead of a half-hidden
 * form.
 */
export interface OrgAuthUi {
  /** SSO sign-in buttons shown when the org has SSO configured. */
  showSso: boolean;
  /** Password form shown for ``password_only`` and ``both``. */
  showPassword: boolean;
  /** "Or" divider only when both methods are simultaneously visible. */
  showDivider: boolean;
}

export function deriveOrgAuthUi(orgConfig: OrgConfigResponse | null): OrgAuthUi {
  if (!orgConfig) {
    return { showSso: false, showPassword: false, showDivider: false };
  }
  // auth_mode is canonical: it encodes the policy the org admin selected.
  // sso_enabled is treated as "and only if SSO is actually wired" — i.e.
  // a ``both`` org with no IdPs configured just shows the password form.
  // For ``sso_only`` we always show SSO so a transient sso_enabled=false
  // does not strand the user with no login affordance at all.
  const showPassword = orgConfig.auth_mode !== "sso_only";
  const showSso =
    orgConfig.auth_mode === "sso_only" ||
    (orgConfig.auth_mode === "both" && orgConfig.sso_enabled);
  return { showSso, showPassword, showDivider: showSso && showPassword };
}

let _redirecting = false;

export function redirectToLogin(reason?: string) {
  // Pre-cookie-migration tokens may still live in localStorage from an older
  // build. Purge regardless of mode so a refresh leaves no stale traces.
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);

  // Mock mode never navigates — the SPA owns auth state via mock-provider.
  if (envConfig.enableMockApi) return;

  // Skip redirects when the user is on a page that owns its own auth
  // lifecycle. /sso/callback is mid-exchange: a bootstrap /me 401 here is
  // expected (cookies are about to be set by the in-flight callback POST)
  // and bouncing to /login would cancel the exchange.
  if (
    window.location.pathname === "/login" ||
    window.location.pathname === "/sso/callback"
  ) {
    return;
  }

  if (_redirecting) return;
  _redirecting = true;

  if (reason) sessionStorage.setItem("auth:flash", reason);

  const currentPath = window.location.pathname + window.location.search;
  const redirectParam = currentPath
    ? `?redirect=${encodeURIComponent(currentPath)}`
    : "";
  window.location.href = `/login${redirectParam}`;
}
